import { client, client_query_engine } from '../http_client';
import { MarketAggregateResponse, LatestForecastInfoQueryResult } from './types';

export const root = '/v1/analytics';
export const query_engine_root = '/v1/forecast';

// Normalize init time to handle different timezone formats:
// - With 'Z' suffix (UTC) - used as-is
// - With timezone offset (+00:00, -05:00, etc.) - used as-is
// - Without timezone - append 'Z' to treat as UTC
function normalizeInitTime(initTime: string | null | undefined): string | null {
  if (!initTime) return null;
  if (/[Zz]$/.test(initTime) || /[+-]\d{2}:?\d{2}$/.test(initTime)) {
    return initTime;
  }
  return `${initTime}Z`;
}

export class AnalyticsService {
  public static async getLatestRun(
    modelId: string,
    weightType: string,
    demo?: boolean,
  ): Promise<string | null> {
    if (demo) {
      const url = `${root}/demo/latest-run?model=${modelId}&weight_type=${weightType}`;
      const response = await client.get<string>(url);
      return normalizeInitTime(response.data);
    }

    // Require 6 hours of forecast
    const url = `${query_engine_root}/latest-init-time?models=${modelId}&min_prediction_timedelta=360`;
    const response = await client_query_engine.get<LatestForecastInfoQueryResult>(url);
    const initTime = response.data?.forecasts_per_model?.[modelId]?.init_time;
    return normalizeInitTime(initTime);
  }

  public static async getMarketAggregates(
    startTime: Date,
    endTime: Date,
    model?: string,
    zone?: string | string[],
    weightType?: string,
    maxLeadTimeHours?: number,
    demo?: boolean,
  ): Promise<MarketAggregateResponse> {
    const params = new URLSearchParams({
      min_lead_time: '1',
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
    });
    if (model) {
      params.set('model', model);
    }
    if (zone) {
      const zoneParam = Array.isArray(zone) ? zone.join(',') : zone;
      params.set('zone', zoneParam);
    }
    if (weightType) {
      params.set('weight_type', weightType);
    }
    if (typeof maxLeadTimeHours === 'number') {
      params.set('max_lead_time', String(maxLeadTimeHours));
    }
    const endpoint = demo ? `${root}/demo` : root;
    const url = `${endpoint}/market-aggregates?${params}`;
    const response = await client.get<MarketAggregateResponse>(url);
    return response.data;
  }

  public static async getAvailableZones(weightType: string): Promise<string[]> {
    const params = new URLSearchParams({
      weight_type: weightType,
    });
    const url = `${root}/aggregates/available-zones?${params}`;
    const response = await client.get<{ zones: string[] }>(url);
    return response.data.zones;
  }
}
